import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { gql, useQuery } from "@apollo/client";
import { Indent } from "@stenajs-webui/core";
import {
  BreadCrumbs,
  ResultListBanner,
  TabMenu,
  UseResultListBannerStateResult,
  stenaBusinessAgreement,
} from "@stenajs-webui/elements";
import * as React from "react";
import { Crumb } from "@/components/bread-crumbs/Crumb";
import { PageHeader } from "@/components/page-header/PageHeader";
import { Tab } from "@/components/tabs/Tab";
import { TabsRouting } from "../../product-and-price/types/AgreementTabsRouting";
import { routeFactory } from "../../../RouteFactory";
import { useCustomer } from "../../../hooks/UseCustomer";
import { useNegotiationYears } from "../../../contexts/NegotiationYearGate";
import { routeAgreementCanBeEdited } from "@/lib/RouteAgreementCalculator";

import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import {
  RouteAgreementDetailsHeaderQuery,
  RouteAgreementDetailsHeaderQueryVariables,
} from "@/gql/graphql";
import { routeAgreementPageFragment } from "../gql/fragments/RouteAgreementPageFragment";
import { RouteAgreementSaveButton } from "./RouteAgreementSaveButton";
import { SalesReadySwitch } from "./SalesReadySwitch";

interface Props {
  customerId: string;
  headAgreementId: string;
  routePairId: string;
  routeAgreementId: string;
  bannerHook: UseResultListBannerStateResult;
}

export const routeAgreementQuery = gql`
  ${routeAgreementPageFragment}
  query RouteAgreementDetailsHeader($routeAgreementId: ID!) {
    productPrice {
      routeAgreement {
        byId(id: $routeAgreementId) {
          ...RouteAgreementPageRouteAgreement
        }
      }
    }
  }
`;

export const RouteAgreementDetailsHeader: React.FC<Props> = (props) => {
  const {
    customerId,
    routePairId,
    routeAgreementId,
    headAgreementId,
    bannerHook,
  } = props;

  const { loading, data } = useQuery<
    RouteAgreementDetailsHeaderQuery,
    RouteAgreementDetailsHeaderQueryVariables
  >(routeAgreementQuery, {
    variables: {
      routeAgreementId,
    },
  });

  const { defaultYear } = useNegotiationYears();
  const { customer } = useCustomer(customerId, defaultYear);

  const headAgreement = data?.productPrice.routeAgreement.byId?.headAgreement;
  const routeAgreement = data?.productPrice.routeAgreement.byId;

  const path =
    routeFactory.productAndPrice.routeAgreement.routeAgreementDetails(props);

  const hasNote =
    routeAgreement?.externalComment ||
    routeAgreement?.note ||
    routeAgreement?.comment
      ? true
      : false;

  return (
    <PageHeader
      title={"Route agreement"}
      loading={loading}
      icon={stenaBusinessAgreement}
      zIndex={ZIndex.low}
      contentRight={
        routeAgreement && (
          <>
            <SalesReadySwitch
              routeAgreementId={routeAgreementId}
              routeAgreement={routeAgreement}
            />
            <Indent num={2} />
            <RouteAgreementSaveButton
              routeAgreementId={routeAgreementId}
              rowVersion={routeAgreement.rowVersion}
              editableRouteAgreement={routeAgreementCanBeEdited(routeAgreement)}
              bannerHook={bannerHook}
            />
          </>
        )
      }
      contentUnder={
        bannerHook.bannerState && (
          <ResultListBanner
            bannerState={bannerHook.bannerState}
            variant={"error"}
            data-testid={testIdConstants.createRouteRatesErrorBanner}
          />
        )
      }
      breadcrumbs={
        <BreadCrumbs>
          {customer && (
            <Crumb
              label={customer?.breadCrumbName ?? ""}
              path={routeFactory.productAndPrice.customer.customer({
                customerId,
              })}
              dataTestid={testIdConstants.breadcrumbCustomer}
            />
          )}
          <Crumb
            label={String(headAgreement?.headAgreementNumber ?? "")}
            path={routeFactory.productAndPrice.customer.customerHeadAgreement({
              customerId,
              headAgreementId,
            })}
            dataTestid={testIdConstants.breadcrumbHeaderAgreement}
          />
          {headAgreement && (
            <Crumb
              label={routePairId.toUpperCase()}
              path={
                routeAgreement
                  ? routeFactory.productAndPrice.customer.customerRoutePair({
                      customerId,
                      headAgreementId,
                      routePairId,
                    })
                  : undefined
              }
            />
          )}
          {headAgreement && routeAgreement && (
            <Crumb label={String(routeAgreement.agreementNumber)} />
          )}
        </BreadCrumbs>
      }
      tabs={
        <TabMenu>
          <Tab
            label={TabsRouting.Rates}
            path={path + TabsRouting.RatesPath}
            dataTestid={testIdConstants.routeAgreementRatesTab}
          />
          <Tab
            label={TabsRouting.Routes}
            path={path + TabsRouting.RoutesPath}
            dataTestid={testIdConstants.routeAgreementRoutesTab}
          />
          <Tab
            label={TabsRouting.Articles}
            path={path + TabsRouting.ArticlesPath}
            hasBadge={routeAgreement?.hasArticles}
          />
          <Tab
            label={TabsRouting.Notes}
            path={path + TabsRouting.NotesPath}
            hasBadge={hasNote}
          />
          <Tab
            label={TabsRouting.Conditions}
            path={path + TabsRouting.ConditionsPath}
            hasBadge={routeAgreement?.hasConditions}
          />
          <Tab
            label={TabsRouting.Matrix}
            path={path + TabsRouting.MatrixPath}
            hasBadge={routeAgreement?.hasMatrix}
          />
        </TabMenu>
      }
    />
  );
};
